import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  BarChart3, RefreshCw, Loader2, AlertTriangle,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell,
  LineChart as RechartsLineChart, Line, Legend,
} from 'recharts';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { useNavigation } from '../hooks/useMe';
import {
  useInspectionTimestamps, useInspectionAnalytics,
} from '../hooks/api/reports';

const FALLBACK_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

function fmtTimestamp(ts, isoDate) {
  if (isoDate) {
    try { return new Date(isoDate).toISOString().slice(0, 10); } catch { /* fall through */ }
  }
  if (typeof ts === 'string' && /^\d{8}$/.test(ts)) {
    return `${ts.slice(0, 2)}-${ts.slice(2, 4)}-${ts.slice(4)}`;
  }
  return ts;
}

function prettyTitle(key) {
  if (!key) return '';
  return String(key)
    .replace(/^[a-z]/, (m) => m.toUpperCase())
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ');
}

function valueOf(d) {
  return Number(d.value ?? d.count ?? 0);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-2 shadow-lg text-xs">
      {label && <div className="text-foreground font-medium mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="text-foreground font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

/* ============================================================== */
/* Analytics tab                                                   */
/* ============================================================== */
export function Analytics() {
  const navigation = useNavigation();

  const [industryId, setIndustryId] = useState('');
  const [projectId,  setProjectId]  = useState('');
  const [deliverableId, setDeliverableId] = useState('');
  const [timestamp, setTimestamp] = useState('');

  const industries = navigation?.industries || [];
  const projects = useMemo(
    () => industries.find((i) => i.id === industryId)?.projects || [],
    [industries, industryId],
  );
  const deliverables = useMemo(
    () => projects.find((p) => p.id === projectId)?.deliverables || [],
    [projects, projectId],
  );

  const filtersComplete = Boolean(industryId && projectId && deliverableId);

  const tsQuery = useInspectionTimestamps(
    { industry_id: industryId, project_id: projectId, deliverable_id: deliverableId },
    { enabled: filtersComplete },
  );

  const aQuery = useInspectionAnalytics(
    timestamp,
    { industry_id: industryId, project_id: projectId, deliverable_id: deliverableId },
    { enabled: Boolean(filtersComplete && timestamp) },
  );

  const items = tsQuery.data?.items || [];
  const analyticsData = aQuery.data?.data;

  const onIndustryChange = (v) => {
    setIndustryId(v); setProjectId(''); setDeliverableId(''); setTimestamp('');
  };
  const onProjectChange = (v) => {
    setProjectId(v); setDeliverableId(''); setTimestamp('');
  };
  const onDeliverableChange = (v) => {
    setDeliverableId(v); setTimestamp('');
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Analytics
          </h1>
          <p className="text-sm text-muted-foreground">
            Chart-ready data per inspection from <code>analytics.json</code> in S3.
          </p>
        </div>
        <Button variant="outline" onClick={() => { tsQuery.refetch(); aQuery.refetch(); }}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </header>

      <Card className="border-0 bg-card">
        <CardHeader><CardTitle className="text-base">Filter</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Industry">
              <Select value={industryId} onValueChange={onIndustryChange}>
                <SelectTrigger><SelectValue placeholder="All industries" /></SelectTrigger>
                <SelectContent>
                  {industries.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Project">
              <Select value={projectId} onValueChange={onProjectChange} disabled={!industryId}>
                <SelectTrigger>
                  <SelectValue placeholder={industryId ? 'Pick a project' : 'Pick an industry first'} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Deliverable">
              <Select value={deliverableId} onValueChange={onDeliverableChange} disabled={!projectId}>
                <SelectTrigger>
                  <SelectValue placeholder={projectId ? 'Pick a deliverable' : 'Pick a project first'} />
                </SelectTrigger>
                <SelectContent>
                  {deliverables.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Inspection Date">
              <Select
                value={timestamp}
                onValueChange={setTimestamp}
                disabled={!filtersComplete || tsQuery.isLoading || items.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !filtersComplete ? 'Pick a deliverable first'
                      : tsQuery.isLoading ? 'Loading…'
                      : items.length === 0 ? 'No inspection data'
                      : 'Choose a date'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {items.map((it) => (
                    <SelectItem key={it.timestamp} value={it.timestamp}>
                      {fmtTimestamp(it.timestamp, it.date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>

      {!filtersComplete && (
        <PromptCard>Select Industry, Project, and Deliverable to begin.</PromptCard>
      )}
      {filtersComplete && !timestamp && (
        <PromptCard>Pick an inspection date to load its charts.</PromptCard>
      )}

      {timestamp && aQuery.isLoading && (
        <div className="flex items-center gap-2 py-12 justify-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
        </div>
      )}
      {timestamp && aQuery.isError && (
        <div className="py-6 px-6 text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {aQuery.error?.message}
        </div>
      )}

      {analyticsData && <AnalyticsCharts payload={analyticsData} />}
    </div>
  );
}

/* ============================================================== */
/* Chart renderer                                                  */
/* analytics.json shape:                                           */
/*   { byStatus: [...], graphType_byStatus: "donut",               */
/*     byIndustry: [...], graphType_byIndustry: "bar",             */
/*     defectsBySeverity: [...], graphType_defectsBySeverity: "line", */
/*     filesByType: [...], graphType_filesByType: "list" }         */
/* Each data array entry has {name, value|count, fill?}            */
/* ============================================================== */
function AnalyticsCharts({ payload }) {
  const sections = useMemo(() => {
    if (!payload || typeof payload !== 'object') return [];
    return Object.entries(payload)
      .filter(([k, v]) => Array.isArray(v) && !k.startsWith('graphType_'))
      .map(([k, v]) => ({
        key: k,
        title: prettyTitle(k),
        type: payload[`graphType_${k}`] || 'bar',
        data: v,
      }));
  }, [payload]);

  if (sections.length === 0) {
    return <PromptCard>analytics.json contained no chart sections.</PromptCard>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {sections.map((s) => (
        <ChartCard key={s.key} title={s.title} type={s.type} data={s.data} />
      ))}
    </div>
  );
}

function ChartCard({ title, type, data }) {
  const total = data.reduce((acc, d) => acc + valueOf(d), 0);
  return (
    <Card className="border-0 bg-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>{title}</span>
          <span className="text-xs font-normal text-muted-foreground">Total: {total}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {type === 'donut' || type === 'pie'
          ? <DonutChart data={data} />
          : type === 'line'
          ? <SimpleLine data={data} />
          : type === 'list'
          ? <ListChart data={data} />
          : <BarChartView data={data} />
        }
      </CardContent>
    </Card>
  );
}

function colorFor(d, i) {
  return d.fill || FALLBACK_PALETTE[i % FALLBACK_PALETTE.length];
}

function DonutChart({ data }) {
  const series = data.map((d, i) => ({ name: d.name, value: valueOf(d), fill: colorFor(d, i) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <RechartsPieChart>
        <Pie data={series} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
          {series.map((s, i) => <Cell key={i} fill={s.fill} />)}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}

function BarChartView({ data }) {
  const series = data.map((d, i) => ({ name: d.name, value: valueOf(d), fill: colorFor(d, i) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
          {series.map((s, i) => <Cell key={i} fill={s.fill} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SimpleLine({ data }) {
  const series = data.map((d) => ({ name: d.name, value: valueOf(d), fill: d.fill }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <RechartsLineChart data={series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip content={<CustomTooltip />} />
        <Line type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={2} dot={{ r: 4 }} />
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}

function ListChart({ data }) {
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2.5">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(d, i) }} />
            <span className="text-sm font-medium">{d.name}</span>
          </div>
          <span className="text-sm font-semibold">{valueOf(d)}</span>
        </div>
      ))}
    </div>
  );
}

function PromptCard({ children }) {
  return (
    <Card className="border-0 bg-card">
      <CardContent className="py-16 text-center text-sm text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
